import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { userService } from '../../services/user.service';
import { CreateUserRequest, User, UserRole } from '../../types';

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

const roles = [
  UserRole.ADMIN,
  UserRole.FLEET_MANAGER,
  UserRole.FINANCE_OFFICER,
  UserRole.DRIVER,
  UserRole.AUDITOR,
];

const schema = z.object({
  username: z.string().min(1, 'Username is required'),
  email: z.string().optional().refine((val) => !val || val === '' || z.string().email().safeParse(val).success, {
    message: 'Invalid email',
  }),
  fullName: z.string().min(1, 'Full name is required'),
  password: z.string().optional(),
  roles: z.array(z.nativeEnum(UserRole)).min(1, 'Select at least one role'),
});

type FormInput = z.input<typeof schema>;

export const UserFormModal: React.FC<UserFormModalProps> = ({ isOpen, onClose, user }) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: zodResolver(schema),
    defaultValues: {
      roles: [UserRole.FLEET_MANAGER],
    },
  });

  const selectedRoles = watch('roles') || [];

  useEffect(() => {
    if (isOpen) {
      reset({
        username: user?.username || '',
        email: user?.email || '',
        fullName: user?.fullName || '',
        password: '',
        roles: user?.roles || [UserRole.FLEET_MANAGER],
      });
    }
  }, [isOpen, reset, user]);

  const createMutation = useMutation({
    mutationFn: (data: CreateUserRequest) => userService.create(data),
    onSuccess: async () => {
      toast.success(
        "User created successfully!\nDefault password: Transport@2026\nUser must change password on first login",
        { duration: 8000 }
      );
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to create user');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateUserRequest> }) => userService.update(id, data),
    onSuccess: async () => {
      toast.success('User updated');
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to update user');
    },
  });

  const toggleRole = (role: UserRole) => {
    const next = selectedRoles.includes(role)
      ? selectedRoles.filter((r) => r !== role)
      : [...selectedRoles, role];
    setValue('roles', next as any, { shouldValidate: true });
  };

  const onSubmit = (data: FormInput) => {
    const parsed = schema.parse(data);

    if (!user) {
      createMutation.mutate({
        username: parsed.username,
        email: parsed.email || undefined,
        fullName: parsed.fullName,
        roles: parsed.roles,
      });
      return;
    }

    const updateData: Partial<CreateUserRequest> = {
      username: parsed.username,
      email: parsed.email,
      fullName: parsed.fullName,
      roles: parsed.roles,
    };

    if (parsed.password && parsed.password.trim().length >= 6) {
      updateData.password = parsed.password;
    }

    updateMutation.mutate({ id: user.id, data: updateData });
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={user ? 'Edit User' : 'Create User'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={isSaving}>
            {user ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        {!user && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            <p className="font-medium mb-1">Default Password Assignment</p>
            <p>Default password 'Transport@2026' will be assigned.</p>
            <p>User must change password on first login.</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Username" required error={errors.username?.message} {...register('username')} />
          <Input label="Email" type="email" error={errors.email?.message as string} {...register('email')} />
        </div>

        <Input label="Full Name" required error={errors.fullName?.message} {...register('fullName')} />

        {user && (
          <Input
            label="Password (optional)"
            type="password"
            helperText="Leave empty to keep the current password"
            error={errors.password?.message}
            {...register('password')}
          />
        )}

        <div>
          <div className="block text-sm font-medium text-neutral-charcoal mb-2">
            Roles<span className="text-error ml-1">*</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {roles.map((role) => (
              <label key={role} className="flex items-center gap-2 text-sm text-neutral-charcoal">
                <input
                  type="checkbox"
                  checked={selectedRoles.includes(role)}
                  onChange={() => toggleRole(role)}
                />
                <span>{role}</span>
              </label>
            ))}
          </div>
          {errors.roles?.message && <p className="mt-1 text-sm text-error">{errors.roles.message}</p>}
        </div>

        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
};
